import React, { useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../../../../styles/dashboardTheme.css';
import '../../../../styles/managementTheme.css';
import '../../empleados/styles/rrhh.css';
import { usePermissions } from '../../../../utils/permissions';

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

const ANIOS = (() => {
  const base = new Date().getFullYear();
  return [base - 1, base, base + 1];
})();

function EditarModal({ open, row, form, setForm, onClose, onGuardar, saving }) {
  useEffect(() => {
    if (!open) return;
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [open]);

  if (!open || !row) return null;

  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(15,23,42,0.45)' }}
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog modal-md modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">
              Horas — {row.apellidos}, {row.nombres}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); onGuardar(); }}>
            <div className="modal-body pt-0">
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label management-label">Horas calculadas (sistema)</label>
                  <input
                    className="form-control"
                    value={row.horas_calculadas ?? '—'}
                    readOnly
                    disabled
                  />
                </div>
                <div className="col-6">
                  <label className="form-label management-label">Horas definitivas *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    className="form-control"
                    value={form.horas_definitivas}
                    onChange={(e) => setForm((p) => ({ ...p, horas_definitivas: e.target.value }))}
                    required
                    autoFocus
                  />
                </div>
                <div className="col-12">
                  <label className="form-label management-label">Observaciones</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.observaciones}
                    onChange={(e) => setForm((p) => ({ ...p, observaciones: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn management-btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function RegistroHorasView({
  mes, setMes, anio, setAnio,
  registros, loading, error, reload,
  modal, editRow, formHoras, setFormHoras, saving, calculando,
  abrirEditar, cerrarModal,
  handleCalcular, handleGuardar,
  handleCerrar, handleReabrir,
}) {
  const { hasPermission } = usePermissions();
  const canEdit  = hasPermission('rrhh.registro_horas.editar');
  const canClose = hasPermission('rrhh.registro_horas.cerrar');

  const totalHoras = registros.reduce((sum, r) => sum + parseFloat(r.horas_definitivas ?? 0), 0);

  return (
    <section className="management-page management-theme-blue rrhh-wrapper">
      {/* Header */}
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">RRHH / Registro de Horas</p>
          <h4 className="management-title mb-1 d-flex align-items-center gap-2">
            <i className="bi bi-clock-history" />
            Registro de Horas Docentes
          </h4>
          <p className="text-muted mb-0">
            Horas trabajadas por docentes — base para liquidación mensual.
          </p>
        </div>
      </div>

      {/* Filtros de mes/año */}
      <div className="management-card mb-4">
        <div className="d-flex align-items-end gap-3 flex-wrap">
          <div>
            <label className="form-label management-label mb-1">Mes</label>
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 130 }}
              value={mes}
              onChange={(e) => setMes(parseInt(e.target.value, 10))}
            >
              {MESES.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label management-label mb-1">Año</label>
            <select
              className="form-select form-select-sm"
              value={anio}
              onChange={(e) => setAnio(parseInt(e.target.value, 10))}
            >
              {ANIOS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={reload}
            disabled={loading}
          >
            <i className="bi bi-arrow-repeat me-1" />Actualizar
          </button>

          {registros.length > 0 && (
            <div className="ms-auto d-flex align-items-center gap-2">
              <span className="text-muted small">Total horas definitivas:</span>
              <span className="fw-bold text-primary">{totalHoras.toFixed(2)} h</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="management-card rrhh-table-card">
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        )}

        {error && (
          <div className="management-alert d-flex justify-content-between align-items-center">
            <span>{error}</span>
            <button className="btn btn-outline-secondary btn-sm" onClick={reload}>Reintentar</button>
          </div>
        )}

        {!loading && !error && registros.length === 0 && (
          <div className="dashboard-empty text-center py-5">
            <i className="bi bi-people dashboard-empty-icon mb-3" />
            <h5 className="fw-semibold mb-1">No hay docentes activos registrados.</h5>
            <p className="text-muted mb-0">Crea empleados de tipo Docente en el módulo de Empleados.</p>
          </div>
        )}

        {!loading && !error && registros.length > 0 && (
          <div className="management-table-wrapper">
            <table className="table align-middle management-table rrhh-table mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Docente</th>
                  <th>Contratación</th>
                  <th className="text-end">H. Calculadas</th>
                  <th className="text-end">H. Definitivas</th>
                  <th className="text-center">Estado</th>
                  {(canEdit || canClose) && <th className="text-end">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {registros.map((row, idx) => {
                  const isCerrado   = row.estado === 'cerrado';
                  const isCalcThis  = calculando === row.empleado_id;
                  const sinRegistro = !row.registro_id;

                  return (
                    <tr key={row.empleado_id}>
                      <td className="fw-semibold">{idx + 1}</td>
                      <td>
                        <div className="fw-semibold">{row.apellidos}, {row.nombres}</div>
                        {row.puesto_nombre && (
                          <div className="text-muted small">{row.puesto_nombre}</div>
                        )}
                      </td>
                      <td>
                        <ContratacionBadge tipo={row.tipo_contratacion} />
                      </td>
                      <td className="text-end">
                        {sinRegistro ? (
                          <span className="text-muted small">—</span>
                        ) : (
                          <span>{parseFloat(row.horas_calculadas ?? 0).toFixed(2)} h</span>
                        )}
                      </td>
                      <td className="text-end">
                        {sinRegistro ? (
                          <span className="text-muted small">Sin registro</span>
                        ) : (
                          <span className="fw-semibold">
                            {parseFloat(row.horas_definitivas ?? 0).toFixed(2)} h
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        {sinRegistro ? (
                          <span className="badge rounded-pill bg-secondary-subtle text-muted px-3 py-2">
                            Sin registro
                          </span>
                        ) : isCerrado ? (
                          <span className="badge rounded-pill bg-success-subtle text-success px-3 py-2">
                            <i className="bi bi-lock-fill me-1" />Cerrado
                          </span>
                        ) : (
                          <span className="badge rounded-pill bg-warning-subtle text-warning px-3 py-2">
                            Borrador
                          </span>
                        )}
                      </td>
                      {(canEdit || canClose) && (
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-1">
                            {canEdit && !isCerrado && (
                              <>
                                {/* Calcular desde sistema académico */}
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm management-action-btn"
                                  title="Calcular desde sistema académico"
                                  onClick={() => handleCalcular(row)}
                                  disabled={isCalcThis}
                                >
                                  {isCalcThis
                                    ? <span className="spinner-border spinner-border-sm" />
                                    : <i className="bi bi-calculator" />}
                                </button>
                                {/* Editar horas */}
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm management-action-btn"
                                  title="Editar horas"
                                  onClick={() => abrirEditar(row)}
                                >
                                  <i className="bi bi-pencil-square" />
                                </button>
                              </>
                            )}
                            {canClose && !isCerrado && row.registro_id && (
                              <button
                                type="button"
                                className="btn btn-outline-success btn-sm management-action-btn"
                                title="Cerrar registro"
                                onClick={() => handleCerrar(row)}
                              >
                                <i className="bi bi-lock" />
                              </button>
                            )}
                            {canClose && isCerrado && (
                              <button
                                type="button"
                                className="btn btn-outline-warning btn-sm management-action-btn"
                                title="Reabrir registro"
                                onClick={() => handleReabrir(row)}
                              >
                                <i className="bi bi-unlock" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Leyenda */}
      <div className="mt-3 text-muted small d-flex gap-3 flex-wrap">
        <span><i className="bi bi-calculator me-1 text-secondary" />Calcular horas desde sesiones académicas realizadas</span>
        <span><i className="bi bi-pencil-square me-1 text-secondary" />Editar / ingresar horas manualmente</span>
        <span><i className="bi bi-lock me-1 text-success" />Cerrar — finaliza el registro del mes</span>
      </div>

      {/* Modal editar */}
      <EditarModal
        open={modal}
        row={editRow}
        form={formHoras}
        setForm={setFormHoras}
        onClose={cerrarModal}
        onGuardar={handleGuardar}
        saving={saving}
      />
    </section>
  );
}

function ContratacionBadge({ tipo }) {
  const map = {
    nomina:    { label: 'Nómina',    css: 'bg-primary-subtle text-primary' },
    servicios: { label: 'Servicios', css: 'bg-info-subtle text-info' },
    hibrido:   { label: 'Híbrido',   css: 'bg-purple-subtle text-purple' },
  };
  const m = map[tipo] || { label: tipo, css: 'bg-secondary-subtle text-secondary' };
  return <span className={`badge rounded-pill ${m.css}`}>{m.label}</span>;
}
